import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Save, Eye, Lock, Info, User, DollarSign,
  FileText, Shield, AlertTriangle, RotateCcw, Sparkles, Loader2, CheckCircle2, Wand2,
  Image as ImageIcon, Video, FileText as FileIcon, File as GenericFile, ExternalLink, Send,
} from "lucide-react";
import { getHorse, getPreview, syncSmartsheet, generateWriteups, mediaRawUrl, setMediaOrder, suggestMedia, api } from "@/lib/api";
import { VideoUploader } from "@/components/video/VideoUploader";
import { StreamThumb } from "@/components/video/StreamThumb";
import { VideoDownloadButton, MediaDownloadBar, mediaKey } from "@/components/video/VideoDownload";
import { val, fmtDate, isEmpty, statusTone } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { HeightSelect } from "@/components/adtemplate/AdFields";
import { RotateControls } from "@/components/RotateControls";
import { AppHeader } from "@/components/brand/AppHeader";
import { brandShort } from "@/lib/brand";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import AdPreview from "@/components/AdPreview";
import { EmojiCarousel } from "@/components/adtemplate/EmojiCarousel";
import { WorkflowStatusPanel } from "@/components/WorkflowStatus";
import { PublishingTab } from "@/components/PublishingTab";
import { SaleScheduling } from "@/components/SaleScheduling";
import { getApplication } from "@/lib/applyApi";
import { DisclosureReview } from "@/components/adtemplate/DisclosureReview";
import { HistoricalAd } from "@/components/adtemplate/HistoricalAd";

// Application horses: these dates come ONLY from the application's Sale Scheduling (no duplicate editable copies).
const SCHEDULE_FIELD_KEYS = ["preview_start_date", "sale_start_date", "sale_close_date", "sale_close_time", "sale_time_zone"];
const SCHED_MIRROR = ["sale_start_date", "sale_start_time", "sale_close_date", "sale_close_time", "sale_time_zone", "listing_date"];

const HORSE_FIELDS = [
  ["horse_name", "Horse's Barn Name"], ["horse_type", "Horse Type"], ["gender", "Gender"],
  ["age", "Age (raw)"], ["year_born", "Year Born"], ["registered_name", "Registered Name"],
  ["registration_number", "Registration #"], ["sire", "Sire"], ["dam", "Dam"],
  ["horse_color", "Color"], ["height_now", "Height Now (HH)"], ["height_maturity", "Height Maturity (HH)"],
  ["preview_start_date", "Preview Start", "date"], ["sale_start_date", "Bidding Start", "date"],
  ["sale_close_date", "Bidding Close", "date"],
  ["ad_catchy_header", "Ad Catchy Header"],
  ["horse_location_city", "Location City"], ["horse_location_state", "State/Province"],
];

const SALE_FIELDS = [
  ["currency", "Currency"], ["gst_added", "GST Added"], ["coggins", "Coggins Provided"],
  ["vet_check", "Vet Check Provided"], ["xrays_provided", "X-Rays Provided"],
  ["sale_start_date", "Bidding Start", "date"], ["sale_close_date", "Bidding Close", "date"],
  ["sale_close_time", "Sale Close Time"], ["sale_time_zone", "Sale Time Zone"],
];

const SELLER_FIELDS = [
  ["customer_name", "Customer Name"], ["customer_phone", "Customer Phone"],
  ["customer_email", "Customer Email"], ["trainer_name", "Trainer Name"],
  ["trainer_phone", "Trainer Phone"], ["trainer_email", "Trainer Email"], ["horse_location_city", "Location City"],
  ["horse_location_state", "State/Province"], ["country", "Country"],
];

const INTERNAL_LABELS = {
  customer_address: "Customer Address",
  seller_company_name: "Seller Company Name",
  reserve_value: "Reserve Value (private)",
  bid_value: "Bid Value",
  sold_value: "Sold Value",
  cnd_value_sold: "CAD Value Sold",
  buyer: "Buyer",
  buyer_contact: "Buyer Contact",
  buyer_address: "Buyer Address",
  bidder_commission_person: "Bidder Commission Person",
  ad_post_reach: "Group Ad Post Reach",
  ba_ad_post_reach: "BA Ad Post Reach",
  total_views: "Total Views",
  expense_ad: "Ad Expense",
  ad_revised_times: "Ad Revised # Times",
  steele_pay_brokerage_fees: "Seller Pays Brokerage Fees",
  contract_status: "Contract Status",
  contract_pdf_url: "Contract PDF URL",
  contract_sent_date: "Contract Sent Date",
  contract_sent: "Contract Sent",
  contract_signed: "Contract Signed",
  ad_weeks: "Ad # Weeks",
  ad_post_status: "Ad Post Status",
  ad_post_invoice: "Ad Post Invoice",
  ad_invoice_sent: "Ad Invoice Sent",
  ad_sell_status: "Ad Sell Status",
  ad_sell_invoice: "Ad Sell Invoice",
  ad_sell_invoice_sent: "Ad Sell Invoice Sent",
  percent_charged: "% Charged",
  not_charging: "Not Charging",
  ad_fee: "Ad Fee",
  ad_fee_charged: "Ad Fee Charged",
  charge_amount: "Charge Amount",
  above_reserve: "Above Reserve",
  notes: "Notes",
  ad_template_status: "Ad Template Status",
  ad_template_edit_link: "Ad Template Edit Link",
  ad_template_sent_date: "Ad Template Sent Date",
};

function EditField({ label, value, onChange, type = "text", mono, sourceChange, onUseSource }) {
  return (
    <div className="min-w-0">
      <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1">
        {label}
      </label>
      <Input
        data-testid={`field-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`h-9 border-stone-200 focus-visible:ring-amber-800 ${mono ? "font-mono text-sm" : ""}`}
      />
      {sourceChange !== undefined && (
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span className="truncate">Smartsheet now says “{String(sourceChange)}”</span>
          <button
            type="button"
            onClick={onUseSource}
            className="ml-auto inline-flex items-center gap-0.5 font-semibold underline hover:text-amber-900"
          >
            <RotateCcw className="h-3 w-3" /> Use source
          </button>
        </div>
      )}
    </div>
  );
}

function ReadField({ label, value, mono }) {
  if (isEmpty(value)) return null;
  const display = typeof value === "boolean" ? (value ? "Yes" : "No") : value;
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">{label}</div>
      <div className={`text-sm text-stone-800 break-words ${mono ? "font-mono" : ""}`}>{display}</div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="h-4 w-4 text-amber-800" />}
        <h3 className="font-display text-base font-bold text-stone-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function AdBuilder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  return <AdBuilderView horseId={id} initialTab={params.get("tab")} focusSection={params.get("section")} />;
}

// One Ad Builder: the full page above, or embedded in the Pipeline V2 Horse Workspace (no page header / fixed bars).
export function AdBuilderView({ horseId: id, embedded = false, onSaved, onDirtyChange, initialTab, focusSection, only }) {
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);

  const [form, setForm] = useState({
    horse_info: {}, seller_info: {}, sale_info: {}, write_up_final: "",
    ai_version_1: "", ai_version_2: "", ai_version_3: "", selected_version: null,
  });
  const [dirty, setDirty] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regenWarnOpen, setRegenWarnOpen] = useState(false);

  const hydrate = (h) => {
    setHorse(h);
    const w = h.write_up || {};
    setForm({
      horse_info: { ...(h.horse_info || {}) },
      seller_info: { ...(h.seller_info || {}) },
      sale_info: { ...(h.sale_info || {}) },
      write_up_final: (w.final_edited ?? "") || "",
      ai_version_1: w.ai_version_1 || "",
      ai_version_2: w.ai_version_2 || "",
      ai_version_3: w.ai_version_3 || "",
      selected_version: w.selected_version ?? null,
    });
    setDirty(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      hydrate(await getHorse(id));
    } catch {
      toast.error("Could not load horse");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const [app, setApp] = useState(null);
  const appId = horse?.application_id;
  useEffect(() => { if (appId) { getApplication(appId).then(setApp).catch(() => setApp(null)); } }, [appId]);
  // Schedule saved/cleared: refresh only the mirrored date keys so unsaved edits elsewhere are kept.
  const onSchedule = async (a) => {
    setApp(a);
    try {
      const h = await getHorse(id);
      const pick = (s) => Object.fromEntries(SCHED_MIRROR.map((k) => [k, (s || {})[k]]));
      setHorse((cur) => ({ ...cur, sale_info: { ...(cur.sale_info || {}), ...pick(h.sale_info) } }));
      setForm((f) => ({ ...f, sale_info: { ...f.sale_info, ...pick(h.sale_info) } }));
      if (previewOpen) setPreview(await getPreview(id));
    } catch { /* card already shows the saved schedule */ }
    onSaved?.();
  };

  const setField = (section, field, value) => {
    setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
    setDirty(true);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncSmartsheet();
      await load();
      toast.success("Resynced from Smartsheet", {
        description: res.source_conflicts
          ? `${res.source_conflicts} horse(s) have source changes to review.`
          : "Local edits preserved.",
      });
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    if (horse?.listing_status === "published" && !window.confirm("Saving these changes will update the website listing but will not change existing Facebook posts.")) return;
    setSaving(true);
    try {
      const updated = await api
        .patch(`/horses/${id}`, {
          horse_info: form.horse_info,
          seller_info: form.seller_info,
          sale_info: form.sale_info,
          write_up_final: form.write_up_final,
          ai_version_1: form.ai_version_1,
          ai_version_2: form.ai_version_2,
          ai_version_3: form.ai_version_3,
          selected_version: form.selected_version,
        })
        .then((r) => r.data);
      hydrate(updated);
      onSaved?.(updated);
      toast.success("Working values saved", { description: "Smartsheet was not modified." });
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setEmoji = async (v) => {
    setForm((f) => ({ ...f, horse_info: { ...f.horse_info, headline_emoji: v } }));
    try {
      await api.patch(`/horses/${id}`, { horse_info: { headline_emoji: v } });
      setHorse((h) => ({ ...h, horse_info: { ...(h.horse_info || {}), headline_emoji: v } }));
      if (previewOpen) setPreview(await getPreview(id));
    } catch {
      toast.error("Could not save the headline emoji");
    }
  };

  const openPreview = async () => {
    try {
      setPreview(await getPreview(id));
      setPreviewOpen(true);
    } catch {
      toast.error("Could not build preview");
    }
  };

  const hasSource = () =>
    !!(horse?.write_up?.original || "").trim() || !!(form.write_up_final || "").trim();
  const versionsExist = () => !!(form.ai_version_1 || form.ai_version_2 || form.ai_version_3);
  const versionsEdited = () => {
    const w = horse?.write_up || {};
    return [1, 2, 3].some((n) => (form[`ai_version_${n}`] || "") !== (w[`ai_version_${n}`] || ""));
  };

  const runGenerate = async () => {
    setRegenWarnOpen(false);
    setGenerating(true);
    try {
      const orig = (horse?.write_up?.original || "").trim();
      const body = orig ? {} : { source_override: form.write_up_final || "" };
      const w = await generateWriteups(id, body);
      setForm((f) => ({
        ...f,
        ai_version_1: w.ai_version_1 || "",
        ai_version_2: w.ai_version_2 || "",
        ai_version_3: w.ai_version_3 || "",
      }));
      setHorse((h) => ({ ...h, write_up: { ...(h.write_up || {}), ...w } }));
      toast.success("3 AI versions generated", { description: "Original Seller Write-Up is untouched." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (!hasSource()) {
      toast.error("No seller write-up yet — enter source text in the Final Write-Up box first.");
      return;
    }
    if (versionsExist() && versionsEdited()) {
      setRegenWarnOpen(true);
      return;
    }
    runGenerate();
  };

  const selectOriginal = async () => {
    const text = (horse?.write_up?.original) || "";
    setForm((f) => ({ ...f, write_up_final: text, selected_version: "original" }));
    try {
      const updated = await api
        .patch(`/horses/${id}`, { write_up_final: text, selected_version: "original" })
        .then((r) => r.data);
      setHorse((h) => ({ ...h, write_up: updated.write_up }));
      toast.success("Original Seller Write-Up is now the Final Write-Up");
    } catch {
      toast.error("Could not select the original write-up");
    }
  };

  const selectVersion = async (n) => {
    const text = form[`ai_version_${n}`] || "";
    setForm((f) => ({ ...f, write_up_final: text, selected_version: n }));
    try {
      const updated = await api
        .patch(`/horses/${id}`, { write_up_final: text, selected_version: n, [`ai_version_${n}`]: text })
        .then((r) => r.data);
      setHorse((h) => ({ ...h, write_up: updated.write_up }));
      toast.success(`Version ${n} is now the Final Write-Up`);
    } catch {
      toast.error("Could not select version");
    }
  };

  const srcChanges = horse?.source_changes || {};
  const conflictCount = useMemo(
    () => Object.values(srcChanges).reduce((n, sec) => n + Object.keys(sec || {}).length, 0),
    [srcChanges]
  );

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }
  if (!horse) return null;

  const hi = horse.horse_info || {};
  const internal = horse.internal_info || {};
  const wu = horse.write_up || {};
  const reserveExists = !isEmpty(internal.reserve_value) && !["0", "$0.00"].includes(String(internal.reserve_value));

  const scOf = (section, field) => srcChanges[section]?.[field];

  const renderFields = (section, fields) => (
    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-4">
      {fields.map(([field, label, type]) => field.startsWith("height_") ? (
        <HeightSelect key={field} label={label} testid={`field-${section}-${field}`} value={form[section][field]} onChange={(v) => setField(section, field, v)} />
      ) : (
        <EditField
          key={field}
          label={label}
          type={type || "text"}
          mono={field.includes("phone") || field.includes("registration")}
          value={form[section][field]}
          onChange={(v) => setField(section, field, v)}
          sourceChange={scOf(section, field)}
          onUseSource={() => setField(section, field, scOf(section, field))}
        />
      ))}
    </div>
  );

  const ActionButtons = ({ compact }) => (
    <>
      <Button
        data-testid={compact ? "resync-button-mobile" : "resync-button-detail"}
        onClick={handleSync}
        disabled={syncing}
        variant={compact ? "ghost" : "outline"}
        size={compact ? "sm" : "default"}
        className={compact ? "flex-col h-auto min-h-[48px] py-1.5 text-white hover:bg-white/10 gap-0.5" : "rounded-full border-stone-300"}
      >
        <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""} ${compact ? "" : "mr-2"}`} />
        <span className={compact ? "text-[10px]" : ""}>Resync</span>
      </Button>
      <Button
        data-testid={compact ? "save-button-mobile" : "save-button"}
        onClick={handleSave}
        disabled={saving || !dirty}
        variant={compact ? "ghost" : "outline"}
        size={compact ? "sm" : "default"}
        className={compact
          ? `flex-col h-auto min-h-[48px] py-1.5 gap-0.5 ${dirty ? "text-amber-300" : "text-white"} hover:bg-white/10`
          : `rounded-full ${dirty ? "border-amber-800 text-amber-900" : "border-stone-300"}`}
      >
        <Save className={`h-4 w-4 ${compact ? "" : "mr-2"}`} />
        <span className={compact ? "text-[10px]" : ""}>{dirty ? "Save*" : "Save"}</span>
      </Button>
      <Button
        data-testid={compact ? "preview-ad-button-mobile" : "preview-ad-button"}
        onClick={openPreview}
        size={compact ? "sm" : "default"}
        className={compact
          ? "flex-col h-auto min-h-[48px] py-1.5 bg-amber-700 hover:bg-amber-600 gap-0.5"
          : "rounded-full bg-amber-800 hover:bg-amber-900 text-white"}
      >
        <Eye className={`h-4 w-4 ${compact ? "" : "mr-2"}`} />
        <span className={compact ? "text-[10px]" : ""}>Preview Ad</span>
      </Button>
    </>
  );

  return (
    <div data-testid={embedded ? "workspace-ad-builder" : undefined} className={embedded ? "overflow-x-clip" : "min-h-screen pb-24 sm:pb-10 overflow-x-clip"}>
      {embedded ? null : (
      <AppHeader title={val(hi.horse_name, "Unnamed Horse")} eyebrow="Ad Builder" onBack={() => navigate("/")} width="max-w-5xl">
          {hi.status && (
            <Badge className={`border ${statusTone(hi.status)} hidden sm:inline-flex`}>{hi.status}</Badge>
          )}
          <div className="hidden sm:flex items-center gap-2">
            <ActionButtons />
          </div>
      </AppHeader>
      )}

      <main className={embedded ? "" : "max-w-5xl mx-auto px-4 sm:px-8 pt-4 sm:pt-6"}>
        <div className="text-xs text-stone-400 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono">Smartsheet Row: {horse.smartsheet_row_id}</span>
          <span>·</span>
          <span>Source: {horse.source}</span>
          <span>·</span>
          <span>Synced {fmtDate(horse.last_synced_date)}</span>
          {hi.age_display || horse.age_display ? (
            <>
              <span>·</span>
              <span className="text-amber-800 font-semibold">Age Display: {horse.age_display}</span>
            </>
          ) : null}
        </div>

        {conflictCount > 0 && (
          <div
            data-testid="source-conflict-banner"
            className="mb-4 flex items-center gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Smartsheet changed {conflictCount} field(s) after your edits. Review the flags below and choose which value to keep.
          </div>
        )}

        <WorkflowStatusPanel
          horse={horse}
          onChange={(h) => setHorse((cur) => ({ ...cur, workflow_status: h.workflow_status, workflow_overrides: h.workflow_overrides }))}
        />

        <Tabs defaultValue={only || initialTab || "horse"} className="w-full">
          {embedded && !only && horse.listing_status === "published" && (
            <div data-testid="workspace-ad-published-banner" className="mb-2 text-xs rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-900 px-3 py-2">
              <b>This advertisement is already published.</b> Saving updates the website listing (you'll be asked to confirm) and is logged with your name and time. Existing Facebook Group and Page posts are never changed.
            </div>
          )}
          {/* Embedded in Horse Workspace: section tabs + Save stay pinned directly below the workspace tabs. */}
          <div data-testid={embedded ? "workspace-ad-sticky-nav" : undefined} className={only ? "hidden" : embedded ? "sticky top-[6.75rem] z-[4] -mx-1 px-1 pt-1 pb-2 bg-[#F3F3F1] space-y-1.5" : ""}>
          {embedded && (
            <div data-testid="workspace-ad-toolbar" className="flex items-center gap-2 overflow-x-auto no-scrollbar [&_button]:h-9 [&_button]:shrink-0">
              <ActionButtons />
              {dirty && <span data-testid="workspace-ad-dirty" className="text-xs font-semibold text-amber-800">Unsaved changes</span>}
            </div>
          )}
          <TabsList className="w-full flex overflow-x-auto hide-scrollbar justify-start bg-stone-100 p-1 rounded-lg h-12 sm:h-10 [&>button]:h-10 sm:[&>button]:h-8">
            <TabsTrigger data-testid="tab-horse-info" value="horse" className="data-[state=active]:bg-snow data-[state=active]:text-ink whitespace-nowrap">Horse Info</TabsTrigger>
            {!embedded && <TabsTrigger data-testid="tab-publishing" value="publishing" className="data-[state=active]:bg-snow data-[state=active]:text-ink whitespace-nowrap gap-1.5"><Send className="h-3.5 w-3.5" />Publishing</TabsTrigger>}
            <TabsTrigger data-testid="tab-writeup" value="writeup" className="data-[state=active]:bg-snow data-[state=active]:text-ink whitespace-nowrap">Write-Up</TabsTrigger>
            <TabsTrigger data-testid="tab-sale" value="sale" className="data-[state=active]:bg-snow data-[state=active]:text-ink whitespace-nowrap">Sale Info</TabsTrigger>
            <TabsTrigger data-testid="tab-seller" value="seller" className="data-[state=active]:bg-snow data-[state=active]:text-ink whitespace-nowrap">Seller Info</TabsTrigger>
            {!embedded && <TabsTrigger data-testid="tab-media" value="media" className="data-[state=active]:bg-snow data-[state=active]:text-ink whitespace-nowrap">Media</TabsTrigger>}
            <TabsTrigger data-testid="tab-internal" value="internal" className="data-[state=active]:bg-snow data-[state=active]:text-ink whitespace-nowrap">Internal</TabsTrigger>
          </TabsList>
          </div>

          {/* SECTION 1 */}
          <TabsContent value="horse" className="mt-5">
            <SectionCard title="Horse Information" icon={Shield}>
              <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
                {horse.age_display && (
                  <span data-testid="age-display" className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-3 py-1 font-semibold">
                    Age Display: {horse.age_display}
                  </span>
                )}
                {horse.gender_display && (
                  <span data-testid="gender-display" className="inline-flex items-center gap-1.5 bg-stone-100 border border-stone-200 text-stone-700 rounded-full px-3 py-1 font-semibold">
                    Gender Display: {horse.gender_display}
                  </span>
                )}
              </div>
              {horse.registration_discrepancy?.fields && (
                <div data-testid="registration-discrepancy" role="alert" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <strong>Registration differs from the signed agreement — please review.</strong> The signed agreement has not been changed.
                  <ul className="mt-1 text-xs">{Object.entries(horse.registration_discrepancy.fields).map(([l, v]) => <li key={l}>{l}: signed “{v.signed || "—"}” · Ad Template “{v.ad_template}”</li>)}</ul>
                </div>
              )}
              {renderFields("horse_info", horse.application_id ? HORSE_FIELDS.filter(([k]) => !SCHEDULE_FIELD_KEYS.includes(k)) : HORSE_FIELDS)}
              {horse.application_id && <p data-testid="horse-info-dates-note" className="mt-3 text-xs text-stone-500">Ad Post, Bidding Start and Bidding Close dates are managed in Sale Info → Sale Scheduling.</p>}
              <div className="mt-4"><EmojiCarousel value={form.horse_info.headline_emoji} onChange={setEmoji} /></div>
              {horse.application_id && <DisclosureReview horseId={id} horse={horse} onSaved={(d) => setHorse((x) => ({ ...x, disclosures: d }))} />}
              <div className="mt-5 flex items-center gap-2 text-xs text-stone-400 border-t border-stone-100 pt-3">
                <Info className="h-3.5 w-3.5" />
                These are your working (application) values. Editing here never changes Smartsheet in Phase 1.
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 2 — Write-Up + AI Rewrite workflow */}
          <TabsContent value="writeup" className="mt-5 space-y-5">
            <HistoricalAd horseId={id} ad={horse.historical_ad} />
            <SectionCard title="Original Seller Write-Up" icon={FileText}>
              <div className="rounded-lg bg-amber-50/60 border border-amber-200 p-4">
                <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-amber-900">
                  <Lock className="h-3.5 w-3.5" /> PRESERVED ORIGINAL — never overwritten by AI or edits
                </div>
                {isEmpty(wu.original) ? (
                  <p className="text-sm text-stone-500 italic">No seller write-up was imported for this horse.</p>
                ) : (
                  <p data-testid="original-writeup" className="text-[15px] leading-relaxed text-stone-800 whitespace-pre-wrap">
                    {wu.original}
                  </p>
                )}
              </div>
              {!isEmpty(wu.original) && (
                <div className="mt-3 flex items-center justify-end gap-2">
                  {form.selected_version === "original" && (
                    <span data-testid="original-selected-badge" className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
                      <CheckCircle2 className="h-3 w-3" /> Selected as Final Write-Up
                    </span>
                  )}
                  <Button
                    data-testid="use-original-button"
                    onClick={selectOriginal}
                    variant={form.selected_version === "original" ? "default" : "outline"}
                    size="sm"
                    className={form.selected_version === "original"
                      ? "rounded-full bg-amber-800 hover:bg-amber-900 text-white"
                      : "rounded-full border-amber-700 text-amber-800 hover:bg-amber-50"}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1.5" /> Use Original Write-Up
                  </Button>
                </div>
              )}
            </SectionCard>

            <SectionCard title="AI Rewrite Versions" icon={Sparkles}>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Button
                  data-testid="generate-ai-button"
                  onClick={handleGenerateClick}
                  disabled={generating}
                  className="rounded-full bg-amber-800 hover:bg-amber-900 text-white"
                >
                  {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                  {generating ? "Generating…" : versionsExist() ? "Regenerate Versions" : "Generate AI Versions"}
                </Button>
                <span className="text-xs text-stone-500 flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5 shrink-0" /> AI rewrites wording only — never facts, dates, or sale terms.
                </span>
              </div>

              {isEmpty(wu.original) && !(form.write_up_final || "").trim() && (
                <div data-testid="no-source-hint" className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                  Not enough seller write-up to generate from. Type source information in the Final Write-Up box below, then generate.
                </div>
              )}

              {!versionsExist() ? (
                <p className="text-sm text-stone-500 italic">
                  No AI versions yet. Click “Generate AI Versions” to create three alternatives.
                </p>
              ) : (
                <div className="space-y-4">
                  {[1, 2, 3].map((n) => {
                    const labels = { 1: "Version 1 · Polished", 2: "Version 2 · Sales Focused", 3: `Version 3 · ${brandShort()} Style` };
                    const selected = form.selected_version === n;
                    return (
                      <div
                        key={n}
                        data-testid={`ai-version-card-${n}`}
                        className={`rounded-xl border p-4 ${selected ? "border-amber-500 bg-amber-50/40" : "border-stone-200 bg-white"}`}
                      >
                        <div className="flex items-center gap-2 mb-2 font-semibold text-sm text-stone-800">
                          <Sparkles className="h-3.5 w-3.5 text-amber-700" /> {labels[n]}
                          {selected && (
                            <span data-testid={`selected-badge-${n}`} className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
                              <CheckCircle2 className="h-3 w-3" /> Selected
                            </span>
                          )}
                        </div>
                        <Textarea
                          data-testid={`ai-version-text-${n}`}
                          value={form[`ai_version_${n}`]}
                          onChange={(e) => { setForm((f) => ({ ...f, [`ai_version_${n}`]: e.target.value })); setDirty(true); }}
                          rows={7}
                          className="border-stone-200 focus-visible:ring-amber-800 text-sm"
                        />
                        <div className="mt-2 flex justify-end">
                          <Button
                            data-testid={`use-version-${n}`}
                            onClick={() => selectVersion(n)}
                            variant={selected ? "default" : "outline"}
                            size="sm"
                            className={selected
                              ? "rounded-full bg-amber-800 hover:bg-amber-900 text-white"
                              : "rounded-full border-amber-700 text-amber-800 hover:bg-amber-50"}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Use This Version
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {wu.ai_style_examples_used && wu.ai_style_examples_used.length > 0 && (
                <div data-testid="style-examples-used" className="mt-4 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5">
                    <Sparkles className="h-3 w-3 text-amber-700" /> Style Examples Used (Version 3 diagnostic)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {wu.ai_style_examples_used.map((n, i) => (
                      <span key={i} data-testid={`style-used-${i}`} className="text-[11px] bg-white border border-amber-300 text-amber-900 rounded-full px-2 py-0.5">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {wu.ai_generated_at && (
                <p className="mt-3 text-[11px] text-stone-400">Last generated {fmtDate(wu.ai_generated_at)}</p>
              )}
            </SectionCard>

            <SectionCard title="Final Write-Up" icon={CheckCircle2}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold">
                  Final Write-Up (used in the ad — editable)
                </label>
                {form.selected_version === "original"
                  ? <span className="text-[11px] text-amber-800 font-semibold">From Original Seller Write-Up</span>
                  : form.selected_version
                  ? <span className="text-[11px] text-amber-800 font-semibold">From Version {form.selected_version}</span>
                  : <span className="text-[11px] text-stone-400">Blank → uses Original Seller Write-Up</span>}
              </div>
              <Textarea
                data-testid="final-writeup"
                value={form.write_up_final}
                onChange={(e) => { setForm((f) => ({ ...f, write_up_final: e.target.value })); setDirty(true); }}
                rows={7}
                placeholder="Click “Use This Version” above, or type/paste the final ad copy here. Leave blank to use the Original Seller Write-Up."
                className="border-stone-200 focus-visible:ring-amber-800 text-sm"
              />
              <div className="mt-3 flex items-center gap-2 text-xs text-stone-400 border-t border-stone-100 pt-3">
                <Info className="h-3.5 w-3.5 shrink-0" /> The ad preview uses the Final Write-Up when present, otherwise the Original. Saved application-side only — never written to Smartsheet.
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 3 */}
          <TabsContent value="sale" className="mt-5">
            <SectionCard title="Sale Information" icon={DollarSign}>
              <div className="mb-4">
                <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">Reserve</div>
                <div className="text-sm text-stone-800">{reserveExists ? "Reserve" : "No Reserve"}</div>
              </div>
              {horse.application_id && app && (embedded
                ? <p data-testid="workspace-sale-dates-note" className="mb-5 text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">Sale dates and scheduling are managed in the Workspace <b>Schedule</b> tab.</p>
                : <div className="mb-5"><SaleScheduling app={app} onChange={onSchedule} /></div>)}
              {renderFields("sale_info", horse.application_id ? SALE_FIELDS.filter(([k]) => !SCHEDULE_FIELD_KEYS.includes(k)) : SALE_FIELDS)}
              <div className="mt-5 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <Lock className="h-3.5 w-3.5 shrink-0" />
                Reserve Value is private/internal (Internal tab) and is never shown in the public ad preview.
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 4 */}
          <TabsContent value="seller" className="mt-5">
            <SectionCard title="Seller Information" icon={User}>
              {renderFields("seller_info", SELLER_FIELDS)}
              <div className="mt-5 flex items-center gap-2 text-xs text-stone-400 border-t border-stone-100 pt-3">
                <Info className="h-3.5 w-3.5" />
                Contact fields used to build the public ad. Billing/home address lives in the Internal tab.
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 4b — Media (Smartsheet attachments, read-only) */}
          <TabsContent value="media" className="mt-5">
            <SectionCard title="Media" icon={ImageIcon}>
              <div className="flex items-center gap-2 text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 mb-4">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Smartsheet attachments, app uploads and Cloudflare videos. Number the photos and videos to include them in the ad, in that order.
              </div>
              <AdMediaManager
                horseId={id}
                media={horse.media || []}
                onChange={(m) => setHorse((h) => ({ ...h, media: m }))}
              />
            </SectionCard>
          </TabsContent>

          <TabsContent value="publishing" className="mt-5">
            <PublishingTab
              horse={horse}
              focusSection={initialTab === "publishing" ? focusSection : null}
              onChange={(h) => setHorse((cur) => ({ ...cur, publishing: h.publishing, publishing_destinations: h.publishing_destinations }))}
            />
          </TabsContent>

          {/* SECTION 5 */}
          <TabsContent value="internal" className="mt-5">
            <SectionCard title="Internal Information" icon={Info}>
              <div className="flex items-center gap-2 text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 mb-4">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Business-only fields. Kept separate from the advertisement and never shown publicly.
              </div>
              <Collapsible open={internalOpen} onOpenChange={setInternalOpen}>
                <CollapsibleTrigger asChild>
                  <Button data-testid="toggle-internal" variant="outline" className="w-full justify-between rounded-lg border-stone-300">
                    {internalOpen ? "Hide internal fields" : "Show internal fields"}
                    <Info className="h-4 w-4" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-4">
                    {Object.entries(INTERNAL_LABELS).map(([key, label]) => (
                      <ReadField key={key} label={label} value={internal[key]} />
                    ))}
                  </div>
                  {Object.keys(INTERNAL_LABELS).every((k) => isEmpty(internal[k])) && (
                    <p className="text-sm text-stone-500 italic">No internal data recorded for this horse.</p>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </SectionCard>
          </TabsContent>
        </Tabs>
      </main>

      {/* Mobile sticky action bar */}
      {!embedded && (
      <div data-testid="adbuilder-mobile-actions" className="fixed bottom-0 left-0 right-0 z-50 bg-ink/95 backdrop-blur-md text-white border-t border-white/10 p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:hidden shadow-2xl grid grid-cols-4 gap-1">
        <Button
          data-testid="back-button-mobile"
          onClick={() => navigate("/")}
          variant="ghost"
          size="sm"
          className="flex-col h-auto min-h-[48px] py-1.5 text-white hover:bg-white/10 gap-0.5"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-[10px]">Back</span>
        </Button>
        <ActionButtons compact />
      </div>
      )}

      {/* Preview Ad */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-stone-50">
          <SheetHeader>
            <SheetTitle className="font-display flex items-center gap-2">
              <Eye className="h-4 w-4 text-amber-800" /> Ad Preview
            </SheetTitle>
            <SheetDescription>
              Public advertisement assembled from your working data. Internal fields are never shown.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5">
            <AdPreview preview={preview} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Regenerate warning — protects manual edits */}
      <AlertDialog open={regenWarnOpen} onOpenChange={setRegenWarnOpen}>
        <AlertDialogContent data-testid="regen-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace your edited AI versions?</AlertDialogTitle>
            <AlertDialogDescription>
              You've manually edited one or more AI versions. Regenerating will replace all three with new
              alternatives and your edits to the generated versions will be lost. Your Original Seller Write-Up and
              your Final Write-Up will NOT be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="regen-cancel">Keep my edits</AlertDialogCancel>
            <AlertDialogAction data-testid="regen-confirm" onClick={runGenerate} className="bg-amber-800 hover:bg-amber-900">
              Regenerate anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function fmtSize(kb) {
  if (kb === null || kb === undefined || kb === "") return null;
  const n = Number(kb);
  if (isNaN(n)) return null;
  if (n < 1024) return `${Math.round(n)} KB`;
  return `${(n / 1024).toFixed(1)} MB`;
}

const KIND_META = {
  image: { icon: ImageIcon, label: "Image", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  video: { icon: Video, label: "Video", tone: "text-sky-700 bg-sky-50 border-sky-200" },
  document: { icon: FileIcon, label: "Document", tone: "text-amber-800 bg-amber-50 border-amber-200" },
  other: { icon: GenericFile, label: "File", tone: "text-stone-600 bg-stone-50 border-stone-200" },
};

export function MediaGrid({ horseId, media }) {
  if (!media || media.length === 0) {
    return (
      <div data-testid="media-empty" className="text-center py-12 text-stone-500">
        <ImageIcon className="h-9 w-9 mx-auto mb-2 text-stone-300" />
        <p className="text-sm">No Smartsheet attachments found for this horse.</p>
      </div>
    );
  }
  const counts = media.reduce((a, m) => ((a[m.kind] = (a[m.kind] || 0) + 1), a), {});
  const summary = ["image", "video", "document", "other"]
    .filter((k) => counts[k])
    .map((k) => `${counts[k]} ${KIND_META[k].label.toLowerCase()}${counts[k] > 1 ? "s" : ""}`)
    .join(" · ");

  return (
    <div data-testid="media-grid">
      <div className="text-xs text-stone-500 mb-3">
        {media.length} attachment{media.length > 1 ? "s" : ""} from Smartsheet — {summary}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {media.map((m) => {
          const meta = KIND_META[m.kind] || KIND_META.other;
          const Icon = meta.icon;
          const href = m.external_url || mediaRawUrl(horseId, m.smartsheet_attachment_id, m.rotation);
          const size = fmtSize(m.size_kb);
          return (
            <div
              key={m.smartsheet_attachment_id}
              data-testid={`media-item-${m.smartsheet_attachment_id}`}
              className="rounded-xl border border-stone-200 bg-white overflow-hidden flex flex-col"
            >
              <div className="relative aspect-square bg-stone-100 flex items-center justify-center overflow-hidden">
                {m.kind === "image" ? (
                  <img
                    data-testid={`media-thumb-${m.smartsheet_attachment_id}`}
                    src={href}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                  />
                ) : m.kind === "video" && m.source === "stream" ? (
                  <StreamThumb videoId={m.video_id} name={m.name} className="w-full h-full" />
                ) : m.kind === "video" ? (
                  <video
                    data-testid={`media-video-${m.smartsheet_attachment_id}`}
                    src={href}
                    controls
                    preload="metadata"
                    className="w-full h-full object-cover bg-black"
                  />
                ) : null}
                <div
                  className="absolute inset-0 flex-col items-center justify-center text-stone-400 gap-1"
                  style={{ display: m.kind === "image" || m.kind === "video" ? "none" : "flex" }}
                >
                  <Icon className="h-8 w-8" />
                  <span className="text-[10px] uppercase tracking-wider">{meta.label}</span>
                </div>
                <span className={`absolute top-1.5 left-1.5 text-[10px] font-semibold rounded-full border px-1.5 py-0.5 ${meta.tone}`}>
                  {meta.label}
                </span>
              </div>
              <div className="p-2.5 flex flex-col gap-1 min-w-0">
                <p className="text-xs font-medium text-stone-800 truncate" title={m.name}>{m.name}</p>
                <div className="flex items-center justify-between text-[11px] text-stone-400">
                  <span className="truncate">{m.mime_type || "file"}{size ? ` · ${size}` : ""}</span>
                  <a
                    data-testid={`media-open-${m.smartsheet_attachment_id}`}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 text-amber-800 hover:underline shrink-0 ml-2"
                  >
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <span className="text-[10px] text-stone-300">Source: Smartsheet</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdMediaManager({ horseId, media, onChange }) {
  const [items, setItems] = useState(media || []);
  const [suggesting, setSuggesting] = useState(false);
  useEffect(() => { setItems(media || []); }, [media]);

  const adMedia = items.filter((m) => ["image", "video", "paper"].includes(m.kind))
    .sort((a, b) => (a.ad_order || 9999) - (b.ad_order || 9999));
  const docs = items.filter((m) => m.kind === "document" || m.kind === "other");
  const selectedCount = adMedia.filter((m) => m.ad_order).length;

  const apply = (updated) => { setItems(updated); onChange && onChange(updated); };

  const commitOrder = async (attId, raw) => {
    const n = raw === "" || raw == null ? null : Math.max(1, parseInt(raw, 10) || 0) || null;
    const current = adMedia.find((m) => m.smartsheet_attachment_id === attId);
    if ((current?.ad_order ?? null) === n) return;
    try { apply(await setMediaOrder(horseId, { [attId]: n })); }
    catch { toast.error("Could not update Ad Order"); }
  };

  const suggest = async () => {
    setSuggesting(true);
    try { apply(await suggestMedia(horseId)); toast.success("AI suggested the media & order — review and adjust as you like."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suggest Media failed"); }
    finally { setSuggesting(false); }
  };

  return (
    <div data-testid="ad-media-manager">
      <div className="mb-4"><MediaDownloadBar horseId={horseId} />
        <VideoUploader scope={{ type: "horse", id: horseId }} adminView title="Horse Videos (Cloudflare Stream)"
        onChanged={() => getHorse(horseId).then((h) => apply(h.media || [])).catch(() => {})} /></div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-stone-600">
          <span className="font-semibold text-stone-900">{selectedCount}</span> of {adMedia.length} selected for the ad
          <span className="text-stone-400"> · number a photo/video to include &amp; order it (1 = cover)</span>
        </div>
        <Button data-testid="suggest-media-button" onClick={suggest} disabled={suggesting}
          className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">
          {suggesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
          {suggesting ? "Analyzing…" : "Suggest Media"}
        </Button>
      </div>

      {adMedia.length === 0 ? (
        <div data-testid="media-empty" className="text-center py-10 text-stone-500">
          <ImageIcon className="h-9 w-9 mx-auto mb-2 text-stone-300" />
          <p className="text-sm">No photos or videos on this horse's Smartsheet row.</p>
        </div>
      ) : (
        <div data-testid="ad-media-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {adMedia.map((m) => {
            const href = m.external_url || mediaRawUrl(horseId, m.smartsheet_attachment_id, m.rotation);
            const sel = !!m.ad_order;
            const size = fmtSize(m.size_kb);
            return (
              <div key={m.smartsheet_attachment_id} data-testid={`media-item-${m.smartsheet_attachment_id}`}
                className={`rounded-xl border overflow-hidden flex flex-col ${sel ? "border-amber-500 ring-2 ring-amber-200" : "border-stone-200"}`}>
                <div className="relative aspect-square bg-stone-100 flex items-center justify-center overflow-hidden">
                  {m.kind === "paper" && (m.mime_type || "").includes("pdf") ? (
                    <FileIcon data-testid={`media-pdf-${m.smartsheet_attachment_id}`} className="h-10 w-10 text-red-700" />
                  ) : m.kind !== "video" ? (
                    <img data-testid={`media-thumb-${m.smartsheet_attachment_id}`} src={href} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : m.source === "stream" ? (
                    <StreamThumb videoId={m.video_id} name={m.name} className="w-full h-full" />
                  ) : (
                    <video data-testid={`media-video-${m.smartsheet_attachment_id}`} src={href} controls preload="metadata" className="w-full h-full object-cover bg-black" />
                  )}
                  {sel && (
                    <span data-testid={`ad-order-badge-${m.smartsheet_attachment_id}`}
                      className="absolute top-1.5 left-1.5 flex items-center gap-1 text-sm font-extrabold text-white bg-amber-700 rounded-full h-8 min-w-8 px-2 shadow justify-center">
                      #{m.ad_order}{m.ad_order === 1 && <span className="text-[9px] font-bold uppercase">Cover</span>}
                    </span>
                  )}
                  <span className="absolute top-1.5 right-1.5 text-[10px] font-semibold rounded-full border bg-white/90 px-1.5 py-0.5 text-stone-600">
                    {m.kind === "video" ? "Video" : m.kind === "paper" ? "Registration Papers" : "Image"}
                  </span>
                </div>
                <div className="p-2.5 flex flex-col gap-2">
                  <p className="text-xs font-medium text-stone-800 truncate" title={m.name}>{m.name}</p>
                  {m.kind === "image" && !m.external_url && <div className="flex justify-end -my-1"><RotateControls horseId={horseId} m={m} testId={`media-rotate-${m.smartsheet_attachment_id}`}
                    onRotated={(r) => { const next = items.map((x) => (x === m ? { ...x, rotation: r } : x)); setItems(next); onChange?.(next); }} /></div>}
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] text-stone-500 shrink-0">Ad Order</label>
                    <input
                      data-testid={`ad-order-input-${m.smartsheet_attachment_id}`}
                      type="number" min="1" inputMode="numeric"
                      key={`${m.smartsheet_attachment_id}-${m.ad_order ?? ""}`}
                      defaultValue={m.ad_order ?? ""}
                      onBlur={(e) => commitOrder(m.smartsheet_attachment_id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      placeholder="—"
                      className="w-14 h-9 text-center rounded-md border border-stone-300 focus:border-amber-700 focus:ring-1 focus:ring-amber-700 text-sm"
                    />
                    {sel && m.kind !== "paper" && (
                      <button data-testid={`ad-order-clear-${m.smartsheet_attachment_id}`}
                        onClick={() => commitOrder(m.smartsheet_attachment_id, "")}
                        className="text-[11px] text-stone-400 hover:text-red-600 ml-auto">Remove</button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <a href={href} target="_blank" rel="noreferrer" className="text-[11px] text-amber-800 hover:underline inline-flex items-center gap-0.5">
                      Open <ExternalLink className="h-3 w-3" />
                    </a>
                    {["image", "video", "paper"].includes(m.kind) && !m.external_url && <VideoDownloadButton horseId={horseId} videoKey={mediaKey(m)} compact testId={`media-download-${m.smartsheet_attachment_id}`} />}
                  </div>
                  <span className="text-[10px] text-stone-300">{size ? `${size} · ` : ""}Source: {m.source === "application" ? (m.kind === "paper" ? "Application (always in ad)" : "Application") : m.source === "stream" ? "Cloudflare Stream" : "Smartsheet"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {docs.length > 0 && (
        <div data-testid="documents-section" className="mt-8">
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Documents</h3>
          <p className="text-xs text-stone-400 mb-3">Administrative files (contracts, PDFs). Not part of the advertisement.</p>
          <div className="space-y-2">
            {docs.map((m) => {
              const href = m.external_url || mediaRawUrl(horseId, m.smartsheet_attachment_id, m.rotation);
              const size = fmtSize(m.size_kb);
              return (
                <a key={m.smartsheet_attachment_id} data-testid={`media-doc-${m.smartsheet_attachment_id}`}
                  href={href} target="_blank" rel="noreferrer"
                  className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5 hover:border-amber-400 transition-colors">
                  <FileIcon className="h-5 w-5 text-amber-800 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-stone-800 truncate">{m.name}</p>
                    <p className="text-[11px] text-stone-400">{m.mime_type || "file"}{size ? ` · ${size}` : ""} · Source: Smartsheet</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-stone-400 shrink-0" />
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

